import React, { useEffect, useState, useMemo } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { getResponses, markAsLost } from "../../utils/api/endpoints/questionnaire";
import usePageTitle from "../../utils/hooks/usePageTitle";
import {
  Search,
  SlidersHorizontal,
  ExternalLink,
  Copy,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Inbox,
  FilterX,
  Loader2
} from "lucide-react";
import {
  SalesPageShell,
  SalesPageHeader,
  SalesPanel,
  SalesBadge,
  SalesLoading,
  SalesButton,
  salesTokens
} from "../../components/sales/sales-ui";

// ── Static lookups & helpers ──────────────────────────────────────────────────

const getTempConfig = (confidence: string | undefined) => {
  if (confidence === "high")   return { label: "Hot",      dot: "bg-red-500",    bar: "bg-red-400",    badge: "bg-red-50 text-red-700 border-red-200" };
  if (confidence === "medium") return { label: "Warm",     dot: "bg-amber-400",  bar: "bg-amber-400",  badge: "bg-amber-50 text-amber-700 border-amber-200" };
  if (confidence === "low")    return { label: "Cold",     dot: "bg-blue-400",   bar: "bg-blue-400",   badge: "bg-blue-50 text-blue-700 border-blue-200" };
  return                              { label: "Unscored", dot: "bg-slate-300",  bar: "bg-slate-200",  badge: "bg-slate-50 text-slate-500 border-slate-200" };
};

const getTierBadge = (tier: string | undefined): string => {
  if (tier === "Startup")    return "bg-indigo-50 text-indigo-700 border-indigo-200";
  if (tier === "Growing")    return "bg-violet-50 text-violet-700 border-violet-200";
  if (tier === "Enterprise") return "bg-orange-50 text-orange-700 border-orange-200";
  return "bg-slate-50 text-slate-500 border-slate-200";
};

const relativeDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return "—";
  const diffDays = (Date.now() - new Date(dateStr).getTime()) / 86_400_000;
  if (diffDays < 1)  return "Today";
  if (diffDays < 2)  return "Yesterday";
  if (diffDays < 7)  return `${Math.floor(diffDays)}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  return `${Math.floor(diffDays / 30)}mo ago`;
};

// ── Component ────────────────────────────────────────────────────────────────

const ScopingIntakesPage: React.FC = () => {
  usePageTitle("Prospect Intakes & Scopes");
  const navigate = useNavigate();

  const [loading, setLoading]     = useState(true);
  const [responses, setResponses] = useState<any[]>([]);

  // Filtering States
  const [activeTab, setActiveTab]       = useState<"all" | "public" | "private">("all");
  const [searchQuery, setSearchQuery]   = useState("");
  const [statusFilter, setStatusFilter]   = useState<string>("ALL");
  const [tierFilter, setTierFilter]       = useState<string>("ALL");
  const [tempFilter, setTempFilter]       = useState<string>("ALL");
  const [emailFilter, setEmailFilter]     = useState<string>("ALL");

  const uniqueEmails = useMemo(() => {
    const emails = responses
      .map((r) => r.client_email)
      .filter((email): email is string => !!email);
    return Array.from(new Set(emails)).sort();
  }, [responses]);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 10;

  // Lost Modal State
  const [lostModalOpen, setLostModalOpen] = useState(false);
  const [selectedResponseId, setSelectedResponseId] = useState<string | null>(null);
  const [lostReason, setLostReason] = useState("");
  const [lostNote, setLostNote] = useState("");
  const [submittingLost, setSubmittingLost] = useState(false);

  const handleOpenLostModal = (id: string) => {
    setSelectedResponseId(id);
    setLostReason("");
    setLostNote("");
    setLostModalOpen(true);
  };

  const handleCloseLostModal = () => {
    setLostModalOpen(false);
    setSelectedResponseId(null);
    setLostReason("");
    setLostNote("");
  };

  const handleSubmitLost = async () => {
    if (!selectedResponseId) return;
    if (!lostReason) {
      toast.error("Please select a lost reason");
      return;
    }
    if (lostReason === "Other" && !lostNote.trim()) {
      toast.error("A note is required when the reason is 'Other'");
      return;
    }

    try {
      setSubmittingLost(true);
      await markAsLost(selectedResponseId, lostReason, lostNote);
      toast.success("Marked as Lost successfully");
      handleCloseLostModal();
      loadData();
    } catch (err: any) {
      console.error("Failed to mark as lost:", err);
      toast.error(err.response?.data?.message || "Failed to mark scoping session as lost");
    } finally {
      setSubmittingLost(false);
    }
  };

  // ── Load Data ──────────────────────────────────────────────────────────────

  const loadData = async () => {
    try {
      setLoading(true);
      const respList = await getResponses();
      setResponses(respList);
    } catch (err) {
      console.error("Failed to load intakes list:", err);
      toast.error("Error loading intake sessions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Reset page when any filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, tierFilter, tempFilter, emailFilter, activeTab]);

  // ── Copy Share Link Helper ─────────────────────────────────────────────────

  const handleCopyResponseLink = (shareUrl: string) => {
    navigator.clipboard.writeText(shareUrl);
    toast.success("Intake link copied to clipboard");
  };

  // ── Filters & Search Memo ──────────────────────────────────────────────────

  const filteredResponses = useMemo(() => {
    return responses.filter((r) => {
      // Tab Filter
      if (activeTab === "public") {
        if (r.created_by !== null) return false;
      } else if (activeTab === "private") {
        if (r.created_by === null) return false;
      }

      // 1. Text Search (Client Name / Email)
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesName = (r.client_name || "").toLowerCase().includes(query);
        const matchesEmail = (r.client_email || "").toLowerCase().includes(query);
        if (!matchesName && !matchesEmail) return false;
      }

      // 2. Status Filter
      if (statusFilter === "ALL") {
        if (r.status === "LOST" || r.status === "REJECTED") return false;
      } else {
        if (r.status !== statusFilter) return false;
      }

      // 3. Tier Filter
      if (tierFilter !== "ALL") {
        const tier = r.enrichment?.internalSummary?.tier || "Unscored";
        if (tierFilter === "Unscored") {
          if (tier === "Startup" || tier === "Growing" || tier === "Enterprise") {
            return false;
          }
        } else {
          if (tier !== tierFilter) return false;
        }
      }

      // 4. Temperature Filter
      if (tempFilter !== "ALL") {
        const confidence = r.enrichment?.internalSummary?.tierConfidence || "Unscored";
        if (tempFilter === "Unscored") {
          if (confidence === "high" || confidence === "medium" || confidence === "low") {
            return false;
          }
        } else {
          if (confidence !== tempFilter) return false;
        }
      }

      // 5. Client Email Filter
      if (emailFilter !== "ALL") {
        if (r.client_email !== emailFilter) return false;
      }

      return true;
    });
  }, [responses, searchQuery, statusFilter, tierFilter, tempFilter, emailFilter, activeTab]);

  // ── Paginated Sub-list ─────────────────────────────────────────────────────

  const paginatedResponses = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    return filteredResponses.slice(startIndex, startIndex + PAGE_SIZE);
  }, [filteredResponses, currentPage]);

  const totalPages = Math.ceil(filteredResponses.length / PAGE_SIZE) || 1;

  // Clear all filters action
  const handleClearFilters = () => {
    setSearchQuery("");
    setStatusFilter("ALL");
    setTierFilter("ALL");
    setTempFilter("ALL");
    setEmailFilter("ALL");
  };

  // ── Loading state ──────────────────────────────────────────────────────────

  if (loading) {
    return (
      <SalesPageShell>
        <SalesLoading message="Syncing intakes database…" />
      </SalesPageShell>
    );
  }

  return (
    <SalesPageShell>
      {/* Page Header */}
      <SalesPageHeader
        eyebrow="Scoping Admin"
        title="Prospect Intakes"
        description="Manage and analyze all client scoping questionnaires and assessments."
        actions={
          <div className="flex items-center gap-2 text-[11px] font-semibold text-slate-500 bg-slate-50 border border-slate-100 rounded-lg px-3 py-1.5 shadow-sm">
            <Sparkles size={13} className="text-indigo-500" />
            <span>{filteredResponses.length} sessions listed</span>
          </div>
        }
      />

      {/* Filters Panel */}
      <div className="bg-white border border-slate-150 rounded-xl p-4 shadow-sm mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
        {/* Left Side: Search & Sliders */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 flex-1">
          {/* Search bar */}
          <div className="relative flex-1 max-w-md">
            <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-400 pointer-events-none">
              <Search size={15} />
            </span>
            <input
              type="text"
              placeholder="Search clients by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-slate-200 hover:border-slate-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 rounded-lg text-xs font-medium text-slate-700 bg-slate-50/50 hover:bg-slate-50/20 focus:bg-white placeholder-slate-400 transition"
            />
          </div>

          {/* Quick Clear Filter indicator */}
          {(searchQuery || statusFilter !== "ALL" || tierFilter !== "ALL" || tempFilter !== "ALL" || emailFilter !== "ALL") && (
            <button
              onClick={handleClearFilters}
              className="flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 hover:border-red-200 text-slate-500 hover:text-red-600 bg-white hover:bg-red-50/20 text-xs font-medium transition cursor-pointer self-start sm:self-auto"
            >
              <FilterX size={13} />
              <span>Reset</span>
            </button>
          )}
        </div>

        {/* Right Side: Filters Group */}
        <div className="flex flex-wrap items-center gap-2.5">
          {/* Client Email dropdown */}
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider pl-1">Client Email</label>
            <select
              value={emailFilter}
              onChange={(e) => setEmailFilter(e.target.value)}
              className="py-1.5 pl-2.5 pr-8 border border-slate-200 rounded-lg text-xs font-semibold text-slate-600 bg-white focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition max-w-[180px] truncate"
            >
              <option value="ALL">All Emails</option>
              {uniqueEmails.map((email) => (
                <option key={email} value={email}>
                  {email}
                </option>
              ))}
            </select>
          </div>

          {/* Status dropdown */}
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider pl-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="py-1.5 pl-2.5 pr-8 border border-slate-200 rounded-lg text-xs font-semibold text-slate-600 bg-white focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition"
            >
              <option value="ALL">All Active</option>
              <option value="DRAFT">Draft</option>
              <option value="SUBMITTED">Submitted</option>
              <option value="CONVERTED">Converted</option>
              <option value="REJECTED">Rejected</option>
              <option value="LOST">Lost</option>
            </select>
          </div>

          {/* Tier dropdown */}
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider pl-1">Tier</label>
            <select
              value={tierFilter}
              onChange={(e) => setTierFilter(e.target.value)}
              className="py-1.5 pl-2.5 pr-8 border border-slate-200 rounded-lg text-xs font-semibold text-slate-600 bg-white focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition"
            >
              <option value="ALL">All Tiers</option>
              <option value="Startup">Startup</option>
              <option value="Growing">Growing</option>
              <option value="Enterprise">Enterprise</option>
              <option value="Unscored">Unscored</option>
            </select>
          </div>

          {/* Temperature dropdown */}
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider pl-1">Temperature</label>
            <select
              value={tempFilter}
              onChange={(e) => setTempFilter(e.target.value)}
              className="py-1.5 pl-2.5 pr-8 border border-slate-200 rounded-lg text-xs font-semibold text-slate-600 bg-white focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition"
            >
              <option value="ALL">All Temps</option>
              <option value="high">Hot</option>
              <option value="medium">Warm</option>
              <option value="low">Cold</option>
              <option value="Unscored">Unscored</option>
            </select>
          </div>
        </div>
      </div>

      {/* Tabs Bar */}
      <div className="flex border-b border-slate-200 gap-6 mb-4 px-1 select-none">
        <button
          onClick={() => setActiveTab("all")}
          className={`pb-2.5 text-xs font-bold border-b-2 transition-all cursor-pointer ${
            activeTab === "all"
              ? "text-indigo-600 border-indigo-600 font-extrabold"
              : "text-slate-500 border-transparent hover:text-slate-800"
          }`}
        >
          All Intakes ({responses.filter(r => r.status !== "LOST" && r.status !== "REJECTED").length})
        </button>
        <button
          onClick={() => setActiveTab("public")}
          className={`pb-2.5 text-xs font-bold border-b-2 transition-all cursor-pointer ${
            activeTab === "public"
              ? "text-indigo-600 border-indigo-600 font-extrabold"
              : "text-slate-500 border-transparent hover:text-slate-800"
          }`}
        >
          Public Intakes ({responses.filter(r => r.created_by === null && r.status !== "LOST" && r.status !== "REJECTED").length})
        </button>
        <button
          onClick={() => setActiveTab("private")}
          className={`pb-2.5 text-xs font-bold border-b-2 transition-all cursor-pointer ${
            activeTab === "private"
              ? "text-indigo-600 border-indigo-600 font-extrabold"
              : "text-slate-500 border-transparent hover:text-slate-800"
          }`}
        >
          Private Intakes ({responses.filter(r => r.created_by !== null && r.status !== "LOST" && r.status !== "REJECTED").length})
        </button>
      </div>

      {/* Main Intakes List Panel */}
      <SalesPanel>
        <div className="overflow-x-auto">
          <table className={salesTokens.table}>
            <thead>
              <tr>
                <th className={salesTokens.th}>Client</th>
                <th className={salesTokens.th}>Tier</th>
                <th className={salesTokens.th}>Temperature</th>
                <th className={salesTokens.th}>Status</th>
                <th className={salesTokens.th}>Scope Days</th>
                <th className={salesTokens.th}>Estimated Quote</th>
                <th className={salesTokens.th}>Submitted</th>
                <th className={`${salesTokens.th} text-right`}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedResponses.map((resp) => {
                const tier        = resp.enrichment?.internalSummary?.tier;
                const confidence  = resp.enrichment?.internalSummary?.tierConfidence;
                const tempCfg     = getTempConfig(confidence);
                const tierBadge   = getTierBadge(tier);
                const testingDays = (resp.enrichment?.pricing?.standard?.testingDays  || 0)
                                  + (resp.enrichment?.pricing?.standard?.reportingDays || 0);
                const estPrice    = resp.enrichment?.pricing?.standard?.adjustedPrice;
                const currency    = resp.enrichment?.pricing?.standard?.currency || "NZD";
                const isStale     = resp.status === "DRAFT" && resp.created_at
                  && (Date.now() - new Date(resp.created_at).getTime()) / 86_400_000 > 7;

                return (
                  <tr
                    key={resp.id}
                    className={`${salesTokens.tr} cursor-pointer hover:bg-slate-50/50`}
                    onClick={() => navigate(`/dashboard/sales/scoping/${resp.id}`)}
                  >
                    {/* Client cell with Stale alert */}
                    <td className={salesTokens.td}>
                      <div className="flex items-center gap-2">
                        {isStale && (
                          <span title="Stale draft — no activity in 7+ days">
                            <AlertTriangle size={11} className="text-amber-500 shrink-0" />
                          </span>
                        )}
                        <div>
                          <div className="font-semibold text-slate-800">{resp.client_name || "Awaiting setup"}</div>
                          <div className="text-[10px] text-slate-400 truncate max-w-[180px]">{resp.client_email || "—"}</div>
                        </div>
                      </div>
                    </td>

                    {/* Tier badge */}
                    <td className={salesTokens.td}>
                      {tier ? (
                        <span className={`text-[9px] font-black px-1.5 py-0.5 rounded border ${tierBadge}`}>
                          {tier}
                        </span>
                      ) : (
                        <span className="text-[10px] text-slate-300">—</span>
                      )}
                    </td>

                    {/* Temperature indicator */}
                    <td className={salesTokens.td}>
                      {confidence ? (
                        <div className="flex items-center gap-1.5">
                          <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${tempCfg.dot}`} />
                          <span className="text-[10px] font-semibold text-slate-600">{tempCfg.label}</span>
                        </div>
                      ) : (
                        <span className="text-[10px] text-slate-300">—</span>
                      )}
                    </td>

                    {/* Status badge */}
                    <td className={salesTokens.td}>
                      <SalesBadge status={resp.status} isExpired={resp.isExpired} />
                    </td>

                    {/* Scope Days duration */}
                    <td className={`${salesTokens.td} tabular-nums`}>
                      {testingDays > 0 ? (
                        <span className="text-[11px] font-bold text-indigo-600">{testingDays}d</span>
                      ) : (
                        <span className="text-[10px] text-slate-300">—</span>
                      )}
                    </td>

                    {/* Estimated Quote Price */}
                    <td className={`${salesTokens.td} tabular-nums`}>
                      {estPrice ? (
                        <span className="font-semibold text-slate-700">{currency} ${estPrice.toLocaleString()}</span>
                      ) : (
                        <span className="text-[10px] text-slate-300">—</span>
                      )}
                    </td>

                    {/* Submitted Date */}
                    <td className={`${salesTokens.td} text-[10px] text-slate-500`}>
                      {relativeDate(resp.submitted_at)}
                    </td>

                    {/* Actions dropdown/buttons */}
                    <td className={`${salesTokens.td} text-right`}>
                      <div className="flex items-center justify-end gap-2">
                        {resp.status === "DRAFT" && (
                          <SalesButton
                            variant="secondary"
                            onClick={(e) => { e.stopPropagation(); handleCopyResponseLink(resp.shareUrl); }}
                            title="Copy intake link"
                          >
                            <Copy size={12} />
                          </SalesButton>
                        )}
                        {resp.status !== "CONVERTED" && resp.status !== "REJECTED" && resp.status !== "LOST" && (
                          <SalesButton
                            variant="secondary"
                            onClick={(e) => { e.stopPropagation(); handleOpenLostModal(resp.id); }}
                            className="text-rose-600 hover:text-rose-700 hover:bg-rose-50/50"
                            title="Mark as Lost"
                          >
                            Lost
                          </SalesButton>
                        )}
                        <SalesButton
                          variant="secondary"
                          onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/sales/scoping/${resp.id}`); }}
                        >
                          <ExternalLink size={12} />
                          Open
                        </SalesButton>
                      </div>
                    </td>
                  </tr>
                );
              })}

              {filteredResponses.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-12 text-center text-slate-400 text-xs font-semibold">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <Inbox size={28} className="text-slate-300" />
                      <span>No matching scoping intakes found.</span>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ── Pagination Controls ── */}
        {filteredResponses.length > 0 && (
          <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between bg-slate-50/50 rounded-b-xl">
            {/* Show details */}
            <div className="text-[10px] font-semibold text-slate-500">
              Showing <span className="text-slate-800 font-bold">{Math.min((currentPage - 1) * PAGE_SIZE + 1, filteredResponses.length)}</span> to{" "}
              <span className="text-slate-800 font-bold">{Math.min(currentPage * PAGE_SIZE, filteredResponses.length)}</span> of{" "}
              <span className="text-slate-800 font-bold">{filteredResponses.length}</span> intakes
            </div>

            {/* Nav controls */}
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
                className="w-7 h-7 rounded-lg border border-slate-200 hover:border-slate-300 disabled:opacity-40 disabled:hover:border-slate-200 flex items-center justify-center text-slate-500 hover:text-slate-700 bg-white shadow-sm disabled:cursor-not-allowed transition duration-150"
              >
                <ChevronLeft size={13} />
              </button>

              {/* Page Number Chips */}
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                // Limit chips displayed for huge sets
                if (
                  totalPages > 5 &&
                  page !== 1 &&
                  page !== totalPages &&
                  Math.abs(page - currentPage) > 1
                ) {
                  // Render ellipsis once
                  if (page === 2 && currentPage > 3) {
                    return <span key="el-1" className="px-1 text-slate-400 text-xs select-none">...</span>;
                  }
                  if (page === totalPages - 1 && currentPage < totalPages - 2) {
                    return <span key="el-2" className="px-1 text-slate-400 text-xs select-none">...</span>;
                  }
                  return null;
                }

                return (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-7 h-7 rounded-lg text-xs font-bold transition duration-150 flex items-center justify-center border ${
                      currentPage === page
                        ? "bg-indigo-600 border-indigo-600 text-white shadow-md shadow-indigo-600/10"
                        : "bg-white border-slate-200 hover:border-slate-300 text-slate-600 hover:text-slate-800 shadow-sm"
                    }`}
                  >
                    {page}
                  </button>
                );
              })}

              <button
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="w-7 h-7 rounded-lg border border-slate-200 hover:border-slate-300 disabled:opacity-40 disabled:hover:border-slate-200 flex items-center justify-center text-slate-500 hover:text-slate-700 bg-white shadow-sm disabled:cursor-not-allowed transition duration-150"
              >
                <ChevronRight size={13} />
              </button>
            </div>
          </div>
        )}
      </SalesPanel>

      {/* Lost Modal */}
      {lostModalOpen && ReactDOM.createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl border border-slate-200 shadow-2xl max-w-md w-full overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-200">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-sm font-bold text-slate-900">Mark Prospect Intake as Lost</h3>
              <button
                onClick={handleCloseLostModal}
                className="text-slate-400 hover:text-slate-600 transition text-xs font-semibold cursor-pointer"
              >
                ✕
              </button>
            </div>
            
            <div className="p-5 space-y-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider pl-1">
                  Lost Reason <span className="text-red-500">*</span>
                </label>
                <select
                  value={lostReason}
                  onChange={(e) => setLostReason(e.target.value)}
                  className="w-full py-2 pl-3 pr-8 border border-slate-200 rounded-lg text-xs font-semibold text-slate-700 bg-white focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition"
                >
                  <option value="">Select a reason...</option>
                  <option value="Didn't fill scoping questionnaire">Didn't fill scoping questionnaire</option>
                  <option value="No response / unresponsive">No response / unresponsive</option>
                  <option value="Went with competitor">Went with competitor</option>
                  <option value="Budget / pricing">Budget / pricing</option>
                  <option value="Timing / not now">Timing / not now</option>
                  <option value="Not a fit">Not a fit</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider pl-1">
                  Notes {lostReason === "Other" && <span className="text-red-500">*</span>}
                </label>
                <textarea
                  value={lostNote}
                  onChange={(e) => setLostNote(e.target.value)}
                  placeholder={lostReason === "Other" ? "Please detail the reason (required)..." : "Add any additional context (optional)..."}
                  rows={3}
                  className="w-full p-3 border border-slate-200 rounded-lg text-xs font-medium text-slate-700 bg-white focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition resize-none"
                />
              </div>
            </div>

            <div className="px-5 py-3.5 bg-slate-50/80 border-t border-slate-100 flex items-center justify-end gap-2.5">
              <button
                type="button"
                onClick={handleCloseLostModal}
                className="px-4 py-2 border border-slate-200 hover:border-slate-300 text-slate-600 hover:text-slate-800 text-xs font-bold rounded-lg bg-white shadow-sm transition cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmitLost}
                disabled={submittingLost || !lostReason || (lostReason === "Other" && !lostNote.trim())}
                className="px-4 py-2 bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white text-xs font-bold rounded-lg shadow-md shadow-rose-600/10 transition cursor-pointer flex items-center gap-1.5"
              >
                {submittingLost ? (
                  <>
                    <Loader2 size={13} className="animate-spin" />
                    <span>Submitting...</span>
                  </>
                ) : (
                  <span>Mark Lost</span>
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </SalesPageShell>
  );
};

export default ScopingIntakesPage;
